import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { licensePlateNormalizeFunction, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { removeStringWhiteSpaces } from "@/utils/utils";
import { regexPatterns, validationFunctions, validations } from "@/utils/validationUtils";
import { App as AntApp, AutoComplete, Col, DatePicker, Form, Input, Row, Select, Spin } from "antd";
import { DataSourceItemObject } from "antd/es/auto-complete/AutoComplete";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetVehiclesAutocomplete } from "../../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../../common/constants";
import type { UUID } from "../../../../../../../typings/global";
import type { VehicleCalcVehicleData } from "../../../../../../calculator/calcs/vehicle/types";
import { ELECTRIC_ENGINE_POWER_FUEL_TYPES, SPECS_MAP } from "../../../../../../calculator/calcs/vehicle/utils";
import { ClientType } from "../../../../../../client/enums";
import { Client } from "../../../../../../client/types";
import { formatClientName } from "../../../../../../client/utils";
import { VehicleBrandSelect } from "../../../../../../enumerations/components/form/VehicleBrandSelect";
import VehicleColorSelect from "../../../../../../enumerations/components/form/VehicleColorSelect";
import { VehicleModelSelect } from "../../../../../../enumerations/components/form/VehicleModelSelect";
import { showVehicleFoundModal } from "../../../../../../vehicle/autocomplete/components/VehicleFoundModal";
import { AutocompleteRequestOrigin, AutocompleteVehiclesBy } from "../../../../../../vehicle/autocomplete/enums";
import { Bodywork, FuelType, InsuranceType, Transmission, VehicleCategory } from "../../../../../enums";
import {
  CreateUpdateInsuranceContract,
  CreateUpdateStandardVehicleInsurance,
  CreateUpdateVehicle,
  Vehicle,
  VehicleInsuranceData,
} from "../../../../../types";
import { CrashInsuranceFormPart } from "./CrashInsuranceFormPart";
import { GapInsuranceFormPart } from "./GapInsuranceFormPart";
import { MtplInsuranceFormPart } from "./MtplInsuranceFormPart";
import { StandardVehicleInsuranceFormPart } from "./StandardVehicleInsuranceFormPart";

const AUTOCOMPLETE_MAX_SIZE = 7;
const colSpan = 4;
const colSpanBig = 6;

interface Props {
  clients: (Partial<Client> | undefined)[];
  type: InsuranceType;
  index: number;
}

export const VehicleInsuranceFormPart = ({ index, clients, type }: Props) => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>();
  const [autocompleteVehicleBy, setAutocompleteVehicleBy] = useState<AutocompleteVehiclesBy>(
    AutocompleteVehiclesBy.LICENSE_PLATE,
  );
  const [isOtherBrandChosen, setIsOtherBrandChosen] = useState<boolean>();
  const [isOtherModelChosen, setIsOtherModelChosen] = useState<boolean>();

  //const handledConfirmKeyRef = useRef<string | null>(null);

  const { data: vehicleAutocompleteResult, isFetching } = useGetVehiclesAutocomplete({
    keyword: removeStringWhiteSpaces(autocompleteKeyword?.toUpperCase()),
    by: autocompleteVehicleBy,
    maxResultSize: AUTOCOMPLETE_MAX_SIZE,
    origin: AutocompleteRequestOrigin.CONTRACT,
  });

  const form = Form.useFormInstance<CreateUpdateInsuranceContract>();
  const formNamePrefix = ["insurances", index];
  const formNameDataPrefix = [...formNamePrefix, "insuranceData"];
  const formNameCoveragesPrefix = [...formNameDataPrefix, "coverages"];
  const formNameMtplPrefix = [...formNameDataPrefix, "mtpl"];
  const formNameVehiclePrefix = [...formNamePrefix, "vehicle"];

  const brandId = Form.useWatch<UUID | undefined>([...formNameVehiclePrefix, "brandId"], form);
  const customModel = Form.useWatch<string | undefined>([...formNameVehiclePrefix, "customModel"], {
    form,
    preserve: true,
  });
  const customBrand = Form.useWatch<string | undefined>([...formNameVehiclePrefix, "customBrand"], {
    form,
    preserve: true,
  });
  const category = Form.useWatch<VehicleCategory | undefined>([...formNameVehiclePrefix, "category"], {
    form,
    preserve: true,
  });
  const fuelType = Form.useWatch<FuelType | undefined>([...formNameVehiclePrefix, "fuelType"], {
    form,
    preserve: true,
  });

  const leasing = Form.useWatch<boolean | undefined>([...formNameDataPrefix, "leasing"], form);

  const categorySupports = (spec: keyof VehicleCalcVehicleData) =>
    !!category && !!SPECS_MAP.get(spec)?.includes(category);

  // Until the user picks a brand or model, a custom value loaded with the contract reveals its field
  const hasOtherBrand = isOtherBrandChosen ?? !!customBrand;
  const hasOtherModel = isOtherModelChosen ?? !!customModel;

  const handleAutocompleteSearchDebounced = useMemo(
    () =>
      debounce((value: string, focusedItem: AutocompleteVehiclesBy) => {
        const keyword = removeStringWhiteSpaces(value);

        if (validationFunctions.validateSearchKeyword(keyword)) {
          setAutocompleteKeyword(keyword);
          setAutocompleteVehicleBy(focusedItem);
        }
      }, 500),
    [],
  );

  const handleAutocompleteSearch = useCallback(
    (value: string, focusedItem: AutocompleteVehiclesBy) => {
      handleAutocompleteSearchDebounced(value, focusedItem);
    },
    [handleAutocompleteSearchDebounced],
  );

  const handleAutocompleteSelect = (value: string, focusedItem: AutocompleteVehiclesBy) => {
    setVehicleData(
      vehicleAutocompleteResult?.data.find((vehicle) =>
        focusedItem === AutocompleteVehiclesBy.VIN
          ? vehicle.vin === value
          : vehicle.licensePlate === removeStringWhiteSpaces(value),
      ) as Vehicle,
      focusedItem,
    );
  };

  const handleBrandIdChange = () => {
    const insurances = getInsurances();
    insurances[index] = {
      ...(insurances[index] as CreateUpdateStandardVehicleInsurance),
      vehicle: { ...insurances[index]?.vehicle, modelId: undefined } as CreateUpdateVehicle,
    };
    form.setFieldsValue({ insurances });
  };

  const handleCategoryChange = (category: VehicleCategory) => {
    const insurances = getInsurances();
    const vehicle = insurances[index]?.vehicle;
    insurances[index] = {
      ...(insurances[index] as CreateUpdateStandardVehicleInsurance),
      vehicle: {
        ...(vehicle as CreateUpdateVehicle),
        fuelType: (SPECS_MAP.get("fuelType") as VehicleCategory[]).includes(category) ? vehicle?.fuelType : undefined,
        engineDisplacement: (SPECS_MAP.get("engineDisplacement") as VehicleCategory[]).includes(category)
          ? vehicle?.engineDisplacement
          : undefined,
        enginePower: (SPECS_MAP.get("enginePower") as VehicleCategory[]).includes(category)
          ? vehicle?.enginePower
          : undefined,
        electricEnginePower: (SPECS_MAP.get("electricEnginePower") as VehicleCategory[]).includes(category)
          ? vehicle?.electricEnginePower
          : undefined,
        transmission: (SPECS_MAP.get("transmission") as VehicleCategory[]).includes(category)
          ? vehicle?.transmission
          : undefined,
        bodywork: (SPECS_MAP.get("bodywork") as VehicleCategory[]).includes(category) ? vehicle?.bodywork : undefined,
        seatsNumber: (SPECS_MAP.get("seatsNumber") as VehicleCategory[]).includes(category)
          ? vehicle?.seatsNumber
          : undefined,
        doorsNumber: (SPECS_MAP.get("doorsNumber") as VehicleCategory[]).includes(category)
          ? vehicle?.doorsNumber
          : undefined,
      },
    };
    form.setFieldsValue({ insurances });
  };

  const handleFuelTypeChange = (fuelType: FuelType) => {
    const insurances = getInsurances();
    const vehicle = insurances[index]?.vehicle;
    insurances[index] = {
      ...(insurances[index] as CreateUpdateStandardVehicleInsurance),
      vehicle: {
        ...(vehicle as CreateUpdateVehicle),
        engineDisplacement: fuelType === FuelType.BEV ? undefined : vehicle?.engineDisplacement,
        enginePower: fuelType === FuelType.BEV ? undefined : vehicle?.enginePower,
        electricEnginePower: ELECTRIC_ENGINE_POWER_FUEL_TYPES.includes(fuelType)
          ? vehicle?.electricEnginePower
          : undefined,
      },
    };
    form.setFieldsValue({ insurances });
  };

  const resolveAutocompleteOptions = (focusedItem: AutocompleteVehiclesBy): DataSourceItemObject[] => {
    return focusedItem === vehicleAutocompleteResult?.by
      ? vehicleAutocompleteResult?.data.map<DataSourceItemObject>((vehicle) => ({
          value:
            focusedItem === AutocompleteVehiclesBy.VIN
              ? vehicle.vin
              : (licensePlateNormalizeFunction(vehicle.licensePlate) ?? ""),
          text:
            focusedItem === AutocompleteVehiclesBy.VIN
              ? vehicle.vin
              : (licensePlateNormalizeFunction(vehicle.licensePlate) ?? ""),
        }))
      : [];
  };

  const setVehicleData = (vehicleData: Vehicle, focusedItem?: AutocompleteVehiclesBy) => {
    const vehicle = { ...vehicleData };
    const brandId = vehicle.model?.brand?.id;
    const modelId = vehicle.model?.id;
    const colorId = vehicle.color?.id;

    const insurances = getInsurances();
    const licensePlate = insurances[index]?.licensePlate;

    showVehicleFoundModal({
      modal,
      vehicle,
      licensePlate,
      focusedItem,
      onConfirm: () => {
        const vehicleData: Record<string, any> = { ...vehicle };
        delete vehicleData.model;
        delete vehicleData.color;
        delete vehicleData.certificateCategory;

        // @ts-ignore ts-problem
        insurances[index] = {
          ...(insurances[index] as CreateUpdateStandardVehicleInsurance),
          licensePlate:
            focusedItem === AutocompleteVehiclesBy.VIN && !!licensePlate
              ? licensePlate
              : licensePlateNormalizeFunction(vehicle.licensePlate),
          insuranceData: {
            ...(insurances[index]?.insuranceData as VehicleInsuranceData),
            registrationCertificateNumber: vehicle.registrationCertificateNumber ?? "",
          },
          vehicle: {
            ...(vehicleData as CreateUpdateVehicle),
            firstRegistrationDate: vehicle.firstRegistrationDate,
            brandId,
            modelId,
            colorId,
          },
        };

        form.setFieldsValue({ insurances });

        // A found vehicle with a custom value reveals its field, one without leaves the field as it is shown now
        setIsOtherBrandChosen(!!vehicle.customBrand || hasOtherBrand);
        setIsOtherModelChosen(!!vehicle.customModel || hasOtherModel);
      },
    });
  };

  const getInsurances = (): CreateUpdateStandardVehicleInsurance[] => {
    return [...(form.getFieldValue(["insurances"]) || [])];
  };

  useEffect(() => {
    const vehicle = vehicleAutocompleteResult?.data[0];
    const vehicleAutocompleteBy = vehicleAutocompleteResult?.by;

    const isExactSingleMatch =
      vehicleAutocompleteResult?.data.length === 1 &&
      vehicleAutocompleteResult?.keyword ===
        (vehicleAutocompleteBy === AutocompleteVehiclesBy.VIN ? vehicle?.vin : vehicle?.licensePlate);

    if (!isExactSingleMatch || !vehicle) {
      return;
    }

    setVehicleData(vehicle, vehicleAutocompleteBy);
  }, [vehicleAutocompleteResult]);

  const getInsuranceDataByType = useCallback(() => {
    switch (type) {
      case InsuranceType.MTPL:
        return (
          <MtplInsuranceFormPart
            index={index}
            formNameDataPrefix={formNameDataPrefix}
            formNameCoveragesPrefix={formNameCoveragesPrefix}
            formNamePrefix={formNamePrefix}
          />
        );
      case InsuranceType.CRASH:
        return (
          <CrashInsuranceFormPart
            index={index}
            formNameDataPrefix={formNameDataPrefix}
            formNameCoveragesPrefix={formNameCoveragesPrefix}
            formNamePrefix={formNamePrefix}
            formNameMtplPrefix={formNameMtplPrefix}
          />
        );
      case InsuranceType.GAP:
        return <GapInsuranceFormPart index={index} formNameDataPrefix={formNameDataPrefix} />;
      case InsuranceType.PAS:
        return (
          <Row gutter={rowGutter}>
            <StandardVehicleInsuranceFormPart index={index} formNameDataPrefix={formNameDataPrefix} />
          </Row>
        );
      default:
        return undefined;
    }
  }, [type, index, formNameDataPrefix, formNameCoveragesPrefix, formNamePrefix, formNameMtplPrefix]);

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={24}>
          <b>{t("contract.attrs.insurances.vehicle._label")}</b>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Spin spinning={isFetching && autocompleteVehicleBy === AutocompleteVehiclesBy.LICENSE_PLATE}>
            <Form.Item
              name={[...formNamePrefix, "licensePlate"]}
              label={
                <LabelWithTooltip
                  tooltip={t("calc.vehicle.helpers.licensePlateDesc")}
                  label={t("contract.attrs.insurances.licensePlate")}
                />
              }
              rules={[validations.licensePlate, validations.noRepeatedInsuranceLicensePlate(index)]}
              normalize={licensePlateNormalizeFunction}
            >
              <AutoComplete
                popupMatchSelectWidth={false}
                showSearch={{
                  onSearch: (value) => handleAutocompleteSearch(value, AutocompleteVehiclesBy.LICENSE_PLATE),
                }}
                onSelect={(value: string) => handleAutocompleteSelect(value, AutocompleteVehiclesBy.LICENSE_PLATE)}
                options={resolveAutocompleteOptions(AutocompleteVehiclesBy.LICENSE_PLATE)}
              />
            </Form.Item>
          </Spin>
        </Col>

        <Col span={colSpan}>
          <Spin spinning={isFetching && autocompleteVehicleBy === AutocompleteVehiclesBy.VIN}>
            <Form.Item
              name={[...formNameVehiclePrefix, "vin"]}
              label={t("contract.attrs.insurances.vehicle.vin")}
              rules={[validations.notBlank, validations.size(3, 17), validations.noRepeatedInsuranceVin(index)]}
              normalize={upperCaseStringNormalizeFunction}
            >
              <AutoComplete
                popupMatchSelectWidth={false}
                showSearch={{ onSearch: (value) => handleAutocompleteSearch(value, AutocompleteVehiclesBy.VIN) }}
                onSelect={(value: string) => handleAutocompleteSelect(value, AutocompleteVehiclesBy.VIN)}
                options={resolveAutocompleteOptions(AutocompleteVehiclesBy.VIN)}
              />
            </Form.Item>
          </Spin>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "firstRegistrationDate"]}
            label={t("contract.attrs.insurances.vehicle.firstRegistrationDate")}
            rules={[validations.notNull]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameDataPrefix, "registrationCertificateNumber"]}
            label={t("contract.attrs.insurances.insuranceData.registrationCertificateNumber")}
            rules={[validations.pattern(regexPatterns.registrationCertificateNumberRegex), validations.length(8)]}
            normalize={upperCaseStringNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <VehicleBrandSelect
            formItemProps={{
              name: [...formNameVehiclePrefix, "brandId"],
              label: (
                <LabelWithTooltip
                  label={t("contract.attrs.insurances.vehicle.brandId")}
                  tooltip={t("calc.vehicle.helpers.vehicleBrandDesc")}
                />
              ),
              rules: [validations.notNull],
            }}
            selectProps={{ onChange: handleBrandIdChange }}
            onChangeIsOther={setIsOtherBrandChosen}
          />
        </Col>

        <Col span={colSpan}>
          <VehicleModelSelect
            formItemProps={{
              name: [...formNameVehiclePrefix, "modelId"],
              label: (
                <LabelWithTooltip
                  label={t("contract.attrs.insurances.vehicle.modelId")}
                  tooltip={t("calc.vehicle.helpers.vehicleModelDesc")}
                />
              ),
              rules: [validations.notNull],
            }}
            brandId={brandId}
            selectProps={{ placeholder: !brandId ? t("contract.helpers.vehicleModelPlaceholder") : undefined }}
            onChangeIsOther={setIsOtherModelChosen}
          />
        </Col>
      </Row>

      {hasOtherBrand || hasOtherModel ? (
        <Row gutter={rowGutter}>
          {hasOtherBrand && (
            <Col span={colSpan} offset={colSpan * 4}>
              <Form.Item
                name={[...formNameVehiclePrefix, "customBrand"]}
                label={t("contract.attrs.insurances.vehicle.customBrand")}
                rules={[validations.notBlank, validations.size(1, 255)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}

          {hasOtherModel && (
            <Col span={colSpan} offset={hasOtherBrand ? 0 : colSpan * 5}>
              <Form.Item
                name={[...formNameVehiclePrefix, "customModel"]}
                label={t("contract.attrs.insurances.vehicle.customModel")}
                rules={[validations.notBlank, validations.size(1, 255)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
      ) : undefined}

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "category"]}
            label={t("contract.enums.vehicleCategory._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(VehicleCategory).map((category) => ({
                value: category,
                label: t("contract.enums.vehicleCategory." + category),
              }))}
              onChange={handleCategoryChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "fuelType"]}
            label={t("contract.enums.fuelType._label")}
            rules={[!categorySupports("fuelType") ? validations.none : validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              disabled={!categorySupports("fuelType")}
              options={Object.keys(FuelType).map((type) => ({
                value: type,
                label: t("contract.enums.fuelType." + type),
              }))}
              onChange={handleFuelTypeChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "engineDisplacement"]}
            label={t("contract.attrs.insurances.vehicle.engineDisplacement")}
            rules={[
              validations.minNumber(1),
              !categorySupports("engineDisplacement") || fuelType === FuelType.BEV
                ? validations.none
                : validations.notNull,
            ]}
          >
            <InputNumber
              addon="engineDisplacement"
              disabled={!categorySupports("engineDisplacement") || fuelType === FuelType.BEV}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "enginePower"]}
            label={t("contract.attrs.insurances.vehicle.enginePower")}
            rules={[
              validations.minNumber(1),
              !categorySupports("enginePower") || fuelType === FuelType.BEV ? validations.none : validations.notNull,
            ]}
          >
            <InputNumber addon="enginePower" disabled={!categorySupports("enginePower") || fuelType === FuelType.BEV} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "electricEnginePower"]}
            label={t("contract.attrs.insurances.vehicle.electricEnginePower")}
            rules={[
              validations.minNumber(1),
              categorySupports("electricEnginePower") && fuelType === FuelType.BEV
                ? validations.notNull
                : validations.none,
            ]}
          >
            <InputNumber
              addon="enginePower"
              disabled={
                !categorySupports("electricEnginePower") ||
                !fuelType ||
                !ELECTRIC_ENGINE_POWER_FUEL_TYPES.includes(fuelType)
              }
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "transmission"]}
            label={t("contract.enums.transmission._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!categorySupports("transmission")}
              options={Object.keys(Transmission).map((transmission) => ({
                value: transmission,
                label: t("contract.enums.transmission." + transmission),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "bodywork"]}
            label={t("contract.enums.bodywork._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!categorySupports("bodywork")}
              options={Object.keys(Bodywork).map((bodywork) => ({
                value: bodywork,
                label: t("contract.enums.bodywork." + bodywork),
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "totalWeight"]}
            label={t("contract.attrs.insurances.vehicle.totalWeight")}
            rules={[validations.notNull, validations.minNumber(1)]}
          >
            <InputNumber addon="kg" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "seatsNumber"]}
            label={t("contract.attrs.insurances.vehicle.seatsNumber")}
            rules={[
              validations.minNumber(0),
              !categorySupports("seatsNumber") ? validations.none : validations.notNull,
            ]}
          >
            <InputNumber disabled={!categorySupports("seatsNumber")} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formNameVehiclePrefix, "doorsNumber"]}
            label={t("contract.attrs.insurances.vehicle.doorsNumber")}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber disabled={!categorySupports("doorsNumber")} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <VehicleColorSelect
            formItemProps={{
              name: [...formNameVehiclePrefix, "colorId"],
              label: t("contract.attrs.insurances.vehicle.colorId"),
              rules: [validations.notNull],
            }}
          />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <b>{t("contract.sections.vehicleInsuranceData")}</b>
        </Col>
      </Row>

      {getInsuranceDataByType()}

      <Row gutter={rowGutter}>
        <Col span={colSpanBig}>
          <Form.Item
            name={[...formNamePrefix, "vehicleHolderIdentifier"]}
            label={t("contract.attrs.insurances.vehicleHolderIdentifier")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={clients
                .filter((client): client is Client => !!client)
                .map((c) => ({ value: c.identifier, label: formatClientName(c) }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpanBig}>
          <Form.Item
            name={[...formNamePrefix, "vehicleOwnerIdentifier"]}
            label={
              leasing ? (
                <LabelWithTooltip
                  label={t("contract.attrs.insurances.vehicleOwnerIdentifier")}
                  tooltip={t("contract.helpers.vehicleOwnerClient")}
                />
              ) : (
                t("contract.attrs.insurances.vehicleOwnerIdentifier")
              )
            }
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={clients
                .filter((c, i): c is Client => (leasing ? c?.type === ClientType.LEGAL && i > 0 : !!c))
                .map((c) => ({ value: c.identifier, label: formatClientName(c) }))}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
